import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import LazyLoad from "react-lazyload";

import { UitkHeading } from "uitk-react-text";
import { UitkPlaceholderCard } from "uitk-react-cards";

import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";
import { withStores } from "stores";
import { DestinationVideoProps } from "./typings";
import { DestinationVideoFlexModuleResult } from "typings/microserviceModels/destination-video-flex-module";
import { observer } from "mobx-react";

const DestinationVideo = withStores(
  "analytics",
  "context",
  "flexModuleModelStore"
)(
  observer((props: DestinationVideoProps) => {
    const { flexTemplateNode, flexModuleModelStore, analytics, context } = props;
    const { id } = flexTemplateNode;
    const model = flexModuleModelStore.getModel(id) as DestinationVideoFlexModuleResult | null;

    if (!model) {
      return null;
    }

    const { title, embeddedUrl: videoUrl } = model;
    const { formatText } = useLocalization();

    const alternateTitle = (locationName: string) => formatText("destinationVideo.video.forDestination", locationName);

    // This is covered in e2e test
    /* istanbul ignore next */
    React.useEffect(() => {
      const trackVideoClick = () => {
        const { activeElement } = document;
        if (activeElement?.id === `${id}-destination-video-iframe`) {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: "destination-video",
            action: Action.CLICK,
            rfrr: "video.play",
          };

          sendDelayedTrackEvent(flexTrackingInfo, analytics);
        }
      };

      window.addEventListener("blur", trackVideoClick);

      return () => {
        window.removeEventListener("blur", trackVideoClick);
      };
    }, []);

    return (
      <div className="DestinationVideo uitk-cell all-cell-1-1" id={id}>
        <UitkHeading level={2} size={600}>
          {title || alternateTitle(context.searchContext.location.localizedName)}
        </UitkHeading>

        <div className="all-t-margin-four" data-testid="destination-video-test-iframe">
          <LazyLoad offset={500} height="100%" once={true} placeholder={<UitkPlaceholderCard />}>
            <div className="video-container">
              <iframe
                id={`${id}-destination-video-iframe`}
                title={alternateTitle(context.searchContext.location.defaultName)}
                src={videoUrl}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen={true}
              />
            </div>
          </LazyLoad>
        </div>
      </div>
    );
  })
);

export default DestinationVideo;
